// TopicsStep.tsx — widget del step 'prompt' (multi-tema).
// Qué hace: cargar VARIOS temas con su cantidad de posts. Se escribe el tema,
//   se aprieta Add, el tema queda listado abajo y la caja vuelve en blanco para
//   el siguiente (pedido literal del cliente). Skip = "surprise me", como antes.
// Qué exporta: TopicsStep.
// Quién lo usa: NovahGenerateChat.
import { useRef } from 'react';
import {
  WidgetWrap, PromptInput, ConfirmBtn, SkipBtn, ContactInput,
  TopicRow, TopicCountSelect, RemoveBtn, TopicList, CounterLine, TopicDraftRow,
  AddTopicBtn, TopicsDivider,
} from '../styled';
import { TopicsDraft, MAX_TOPICS, TOPIC_COUNT_OPTIONS } from '../useTopicsDraft';

interface Props {
  topics: TopicsDraft;
  budgetPerPlatform: number;
  confirmTopics: (committed: ReturnType<TopicsDraft['commitDraft']>) => void;
}

export function TopicsStep({ topics, budgetPerPlatform, confirmTopics }: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const {
    list, draftText, draftCount, lastUsed, demand, totalPosts, status,
    setDraftText, setDraftCount, addTopic, updateTopic, removeTopic, applyLastUsed, clear, commitDraft,
  } = topics;

  const full = list.length >= MAX_TOPICS;
  const hasAnything = list.length > 0 || draftText.trim().length > 0;

  const save = () => {
    addTopic();
    inputRef.current?.focus();
  };

  return (
    <WidgetWrap>
      {lastUsed.length > 0 && list.length === 0 && (
        <SkipBtn onClick={applyLastUsed} style={{ marginBottom: 6 }}>
          ↻ Use last time's topics ({lastUsed.length})
        </SkipBtn>
      )}

      {list.length > 0 && (
        <TopicsDivider>{list.length} topic{list.length > 1 ? 's' : ''} added</TopicsDivider>
      )}
      {list.length > 0 && (
        <TopicList>
          {list.map((t, i) => (
            <TopicRow key={t.id}>
              <ContactInput
                value={t.topic}
                aria-label={`Topic ${i + 1}`}
                onChange={(e) => updateTopic(t.id, { topic: e.target.value })}
              />
              <TopicCountSelect
                value={t.count}
                aria-label={`Posts for topic ${i + 1}`}
                onChange={(e) => updateTopic(t.id, { count: Number(e.target.value) })}
              >
                {TOPIC_COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </TopicCountSelect>
              <RemoveBtn type="button" aria-label={`Remove topic ${i + 1}`} onClick={() => removeTopic(t.id)}>
                ×
              </RemoveBtn>
            </TopicRow>
          ))}
        </TopicList>
      )}

      <PromptInput
        ref={inputRef}
        rows={2}
        value={draftText}
        disabled={full}
        placeholder={full
          ? `That's the maximum of ${MAX_TOPICS} topics`
          : list.length === 0 ? 'Summer collection launch, new product...' : 'Next topic...'}
        onChange={(e) => setDraftText(e.target.value)}
        onKeyDown={(e) => {
          // Enter guarda; Shift+Enter sigue siendo un salto de línea.
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); save(); }
        }}
      />
      <TopicDraftRow>
        <TopicCountSelect
          value={draftCount}
          aria-label="Posts for this topic"
          onChange={(e) => setDraftCount(Number(e.target.value))}
        >
          {TOPIC_COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
        </TopicCountSelect>
        <AddTopicBtn type="button" disabled={!draftText.trim() || full} onClick={save}>
          + Add topic
        </AddTopicBtn>
      </TopicDraftRow>

      <CounterLine $tone={status === 'over' ? 'warn' : 'neutral'}>
        {status === 'empty' && `No topics yet — I'll pick from your brand topics.`}
        {status === 'under' && `${demand} of ${budgetPerPlatform} per platform assigned · I'll fill the other ${budgetPerPlatform - demand} from your brand topics · ${totalPosts} posts total`}
        {status === 'exact' && `${demand} per platform · ${totalPosts} posts total`}
        {status === 'over' && `${demand} per platform — ${demand - budgetPerPlatform} more than the ${budgetPerPlatform} you picked. I'll generate all ${totalPosts}.`}
      </CounterLine>

      <ConfirmBtn onClick={() => confirmTopics(commitDraft())} style={{ marginTop: 6 }}>
        {hasAnything ? 'Continue' : 'Skip'}
      </ConfirmBtn>
      {hasAnything && (
        <SkipBtn onClick={() => { clear(); confirmTopics([]); }}>Skip — surprise me</SkipBtn>
      )}
    </WidgetWrap>
  );
}
